import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { Building2, Target, CalendarClock, TrendingUp, Gauge } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Panel, Skeleton } from "./ui";
import Hint from "./Hint";
import { api } from "../lib/api";
import { num, pct, shortDate, gainColor, usd } from "../lib/format";
import { canonicalPrice, upsidePct } from "../lib/price";

const compact = (v) => {
  if (v == null) return "—";
  const a = Math.abs(v);
  if (a >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return num(v);
};

const CONSENSUS_TERM = {
  strong_buy: "Strong Buy", buy: "Buy", hold: "Hold", sell: "Sell",
  strong_sell: "Strong Sell", underperform: "Underperform", none: "No rating",
};
const CONSENSUS_CLS = {
  strong_buy: "bg-emerald-100 text-emerald-700", buy: "bg-emerald-50 text-emerald-600",
  hold: "bg-amber-50 text-amber-700", sell: "bg-rose-50 text-rose-600",
  strong_sell: "bg-rose-100 text-rose-700", underperform: "bg-rose-50 text-rose-600",
  none: "bg-slate-100 text-slate-500",
};

function Stat({ label, term, value, color }) {
  return (
    <div className="py-3 px-1">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
        <Hint term={term || label}>{label}</Hint>
      </div>
      <div className={`mt-1 tabular font-medium ${color || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export function Fundamentals({ ticker, assetType = "EQUITY" }) {
  const { t } = useTranslation();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.fundamentals(ticker).then((r) => setF(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [ticker]);
  if (loading) return <Skeleton className="h-64" />;
  if (!f || !f.name) return null;

  const qt = (f.quote_type || assetType || "EQUITY").toUpperCase();
  const isCrypto = qt.includes("CRYPTO");
  const isFund = qt === "ETF" || qt === "MUTUALFUND";
  const pctStr = (v) => (v != null ? `${(v * 100).toFixed(1)}%` : "—");
  const dyRaw = f.dividend_yield;
  const dy = dyRaw != null ? `${(dyRaw > 1 ? dyRaw : dyRaw * 100).toFixed(2)}%` : "—";

  let title = t("insights.fundamentals");
  let stats;
  if (isCrypto) {
    title = t("insights.marketData");
    stats = [
      { key: "marketCap", term: "Market Cap", value: compact(f.market_cap) },
      { key: "circulatingSupply", term: "Circulating Supply", value: compact(f.circulating_supply) },
      { key: "volume24h", term: "24h Volume", value: compact(f.volume_24h) },
      { key: "dayHigh", term: "Day High", value: f.day_high ? num(f.day_high) : "—" },
      { key: "dayLow", term: "Day Low", value: f.day_low ? num(f.day_low) : "—" },
      { key: "week52High", term: "52W High", value: f.week52_high ? num(f.week52_high) : "—" },
      { key: "week52Low", term: "52W Low", value: f.week52_low ? num(f.week52_low) : "—" },
    ];
  } else if (isFund) {
    title = t("insights.fundOverview");
    stats = [
      { key: "totalAssets", term: "Total Assets", value: compact(f.total_assets) },
      { key: "yield", term: "Yield", value: f.yield != null ? `${(f.yield > 1 ? f.yield : f.yield * 100).toFixed(2)}%` : dy },
      { key: "ytdReturn", term: "YTD Return", value: pctStr(f.ytd_return) },
      { key: "expenseRatio", term: "Expense Ratio", value: pctStr(f.expense_ratio) },
      { key: "nav", term: "NAV", value: f.nav_price ? num(f.nav_price) : "—" },
      { key: "beta", term: "Beta", value: f.beta ? num(f.beta) : "—" },
      { key: "week52High", term: "52W High", value: f.week52_high ? num(f.week52_high) : "—" },
      { key: "week52Low", term: "52W Low", value: f.week52_low ? num(f.week52_low) : "—" },
    ];
  } else {
    stats = [
      { key: "marketCap", term: "Market Cap", value: compact(f.market_cap) },
      { key: "peFwd", term: "P/E (fwd)", value: f.forward_pe ? num(f.forward_pe) : "—" },
      { key: "peTtm", term: "P/E (ttm)", value: f.trailing_pe ? num(f.trailing_pe) : "—" },
      { key: "epsTtm", term: "EPS (ttm)", value: f.eps ? num(f.eps) : "—" },
      { key: "divYield", term: "Div Yield", value: dy },
      { key: "beta", term: "Beta", value: f.beta ? num(f.beta) : "—" },
      { key: "week52High", term: "52W High", value: f.week52_high ? num(f.week52_high) : "—" },
      { key: "week52Low", term: "52W Low", value: f.week52_low ? num(f.week52_low) : "—" },
      { key: "profitMargin", term: "Profit Margin", value: pctStr(f.profit_margins) },
      { key: "roe", term: "ROE", value: pctStr(f.return_on_equity) },
      { key: "revenue", term: "Revenue", value: compact(f.revenue) },
      { key: "avgVolume", term: "Avg Volume", value: compact(f.avg_volume) },
    ];
  }

  return (
    <Panel className="card p-6" testid="instrument-fundamentals">
      <div className="flex items-center gap-2 mb-4"><Building2 size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{title}</h3></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1">
        {stats.map((s) => <Stat key={s.key} label={t(`insights.${s.key}`)} term={s.term} value={s.value} />)}
      </div>
      {(f.sector || f.industry || f.category || f.fund_family) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {f.sector && <span className="pill bg-brand-50 text-brand-600">{f.sector}</span>}
          {f.industry && <span className="pill bg-slate-100 text-slate-600">{f.industry}</span>}
          {f.category && <span className="pill bg-brand-50 text-brand-600">{f.category}</span>}
          {f.fund_family && <span className="pill bg-slate-100 text-slate-600">{f.fund_family}</span>}
        </div>
      )}
      {f.summary && <p className="mt-4 text-sm text-slate-500 leading-relaxed line-clamp-4">{f.summary}</p>}
    </Panel>
  );
}

const CONS_I18N_KEY = { strongBuy: "strong_buy", buy: "buy", hold: "hold", sell: "sell", strongSell: "strong_sell" };

/**
 * `price` is the instrument page's header quote. One screen must show one price:
 * this panel used to render (and compute upside from) the `current` that arrived
 * with the analyst targets, which is cached for six hours against the quote's
 * fifteen minutes — so the header said 57.85 while the upside was worked out
 * against 59.67. The backend now fills `price_targets.current` from the same quote
 * source, and passing the header value in makes the two identical on screen even
 * if the two requests land either side of a cache expiry.
 */
export function AnalystPanel({ ticker, price = null }) {
  const { t } = useTranslation();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.analysis(ticker).then((r) => setA(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [ticker]);
  if (loading) return <Skeleton className="h-64" />;
  if (!a || (!a.price_targets && (!a.trend || a.trend.length === 0))) return null;
  const rawTargets = a.price_targets || {};
  // The header quote wins whenever the page has one — see lib/price.js.
  const pt = { ...rawTargets, current: canonicalPrice(price, rawTargets.current) };
  const consKey = CONSENSUS_TERM[a.recommendation_key] ? a.recommendation_key : "none";
  const range = pt.high && pt.low ? pt.high - pt.low : 0;
  const posOf = (v) => (range ? ((v - pt.low) / range) * 100 : 50);
  const upside = upsidePct(pt.mean, pt.current);
  const latest = a.trend && a.trend.length ? a.trend[0] : null;

  return (
    <Panel className="card p-6" testid="instrument-analyst">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2"><Target size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{t("insights.analystRatings")}</h3></div>
        <span className={`pill ${CONSENSUS_CLS[consKey]}`} data-testid="analyst-consensus">{t(`insights.consensus.${consKey}`)}{a.num_analysts ? ` · ${a.num_analysts}` : ""}</span>
      </div>

      {pt.high && pt.low && (
        <div className="mb-6">
          <div className="flex justify-between text-xs text-slate-400 mb-2">
            <span>{t("insights.low")} {num(pt.low)}</span>
            <span>{t("insights.target")} {num(pt.mean)}{upside != null && <span className={gainColor(upside)}> ({pct(upside)})</span>}</span>
            <span>{t("insights.high")} {num(pt.high)}</span>
          </div>
          <div className="relative h-2 rounded-full bg-gradient-to-r from-rose-200 via-amber-200 to-emerald-300">
            {pt.mean != null && <div className="absolute -top-1 w-1 h-4 bg-brand-500 rounded" style={{ left: `${posOf(pt.mean)}%` }} title="Mean target" />}
            {pt.current != null && <div className="absolute -top-2.5 text-[10px] font-semibold text-slate-700 -translate-x-1/2" style={{ left: `${Math.max(0, Math.min(100, posOf(pt.current)))}%` }}>▼</div>}
          </div>
          <div className="text-xs text-slate-500 mt-1.5 text-center">{t("insights.current")} {num(pt.current)}</div>
        </div>
      )}

      {latest && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{t("insights.recommendationDist")}</div>
          <div className="flex h-8 rounded-lg overflow-hidden text-[11px] font-medium text-white">
            {[["strongBuy", "#059669"], ["buy", "#34d399"], ["hold", "#f59e0b"], ["sell", "#fb7185"], ["strongSell", "#e11d48"]].map(([k, c]) => {
              const total = ["strongBuy", "buy", "hold", "sell", "strongSell"].reduce((s, kk) => s + (latest[kk] || 0), 0) || 1;
              const w = (latest[k] || 0) / total * 100;
              if (w === 0) return null;
              const lbl = t(`insights.consensus.${CONS_I18N_KEY[k]}`);
              return <div key={k} style={{ width: `${w}%`, background: c }} className="flex items-center justify-center" title={`${lbl}: ${latest[k]}`}>{w > 8 ? latest[k] : ""}</div>;
            })}
          </div>
        </div>
      )}
    </Panel>
  );
}

export function EarningsPanel({ ticker }) {
  const { t } = useTranslation();
  const [e, setE] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.earnings(ticker).then((r) => setE(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [ticker]);
  if (loading) return <Skeleton className="h-64" />;
  if (!e || (!e.next_earnings_date && (!e.history || e.history.length === 0))) return null;
  const hist = (e.history || []).filter((h) => h.reported != null).slice(0, 8).reverse();
  return (
    <Panel className="card p-6" testid="instrument-earnings">
      <div className="flex items-center gap-2 mb-4"><CalendarClock size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{t("insights.earnings")}</h3></div>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{t("insights.nextEarnings")}</div>
          <div className="mt-1 font-medium">{e.next_earnings_date ? shortDate(e.next_earnings_date) : "—"}</div>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{t("insights.exDividend")}</div>
          <div className="mt-1 font-medium">{e.ex_dividend_date ? shortDate(e.ex_dividend_date) : "—"}</div>
        </div>
      </div>
      {hist.length > 0 && (
        <>
          <div className="text-xs text-slate-400 mb-2">{t("insights.epsChartHint")}</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={hist} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(2)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => usd(v, { min: 0, max: 2 })} />
              <Tooltip formatter={(v) => usd(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="estimate" name="Estimate" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="reported" name="Reported" radius={[3, 3, 0, 0]}>
                {hist.map((h, i) => <Cell key={i} fill={(h.reported ?? 0) >= (h.estimate ?? 0) ? "#059669" : "#e11d48"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </Panel>
  );
}
